import { useEffect, useRef, useState } from "react";

/** Isolate errors and cancel stale responses per resource/query and login session. */
export function useResource<T>(
  loader: (signal: AbortSignal) => Promise<T>,
  dependencies: readonly unknown[],
) {
  const key = JSON.stringify(dependencies);
  const [state, setState] = useState<{
    key: string;
    data: T | null;
    loading: boolean;
    error: string;
  }>({ key, data: null, loading: true, error: "" });
  const [revision, setRevision] = useState(0);
  const latest = useRef(loader);
  latest.current = loader;
  useEffect(() => {
    const controller = new AbortController();
    setState((previous) => ({
      key,
      data: previous.key === key ? previous.data : null,
      loading: true,
      error: "",
    }));
    void latest
      .current(controller.signal)
      .then((data) => {
        if (!controller.signal.aborted)
          setState({ key, data, loading: false, error: "" });
      })
      .catch((error) => {
        if (!controller.signal.aborted && error?.name !== "AbortError")
          setState((previous) => ({
            ...previous,
            loading: false,
            error: "加载失败，请稍后重试。",
          }));
      });
    return () => controller.abort();
  }, [key, revision]);
  return {
    data: state.key === key ? state.data : null,
    loading: state.key !== key || state.loading,
    error: state.key === key ? state.error : "",
    reload: () => setRevision((v) => v + 1),
    update: (change: T | ((current: T | null) => T)) =>
      setState((previous) => ({
        ...previous,
        key,
        data:
          typeof change === "function"
            ? (change as (current: T | null) => T)(previous.data)
            : change,
      })),
  };
}
/** Keep cursor pages separate and cache visited pages for backwards navigation. */
export function useCursorPagination<T extends { nextCursor: string | null; hasMore: boolean }>(
  loader: (cursor: string | undefined, signal: AbortSignal) => Promise<T>,
  dependencies: readonly unknown[],
) {
  const key = JSON.stringify(dependencies);
  const activeKey = useRef(key);
  activeKey.current = key;
  const first = useResource((signal) => loader(undefined, signal), dependencies);
  const [pagination, setPagination] = useState<{ key: string; pages: T[]; index: number }>({
    key, pages: [], index: 0,
  });
  const [busy, setBusy] = useState(false);
  const [pageError, setPageError] = useState("");
  const pending = useRef<AbortController | null>(null);
  useEffect(() => {
    pending.current?.abort();
    pending.current = null;
    setBusy(false);
    setPageError("");
    setPagination({ key, pages: first.data ? [first.data] : [], index: 0 });
    return () => {
      pending.current?.abort();
      pending.current = null;
    };
  }, [key, first.data]);
  const pages = pagination.key === key && pagination.pages.length
    ? pagination.pages : first.data ? [first.data] : [];
  const index = pagination.key === key ? pagination.index : 0;
  const data = pages[index] ?? null;
  const next = async () => {
    if (pending.current || first.loading || !data?.hasMore || !data.nextCursor) return;
    setPageError("");
    if (pages[index + 1]) {
      setPagination({ key, pages, index: index + 1 });
      return;
    }
    const request = new AbortController();
    pending.current = request;
    setBusy(true);
    try {
      const page = await loader(data.nextCursor, request.signal);
      if (request.signal.aborted || activeKey.current !== key) return;
      setPagination({ key, pages: [...pages, page], index: index + 1 });
    } catch {
      if (!request.signal.aborted && activeKey.current === key)
        setPageError("下一页加载失败，请重试。");
    } finally {
      if (pending.current === request) {
        pending.current = null;
        setBusy(false);
      }
    }
  };
  const previous = () => {
    if (pending.current || first.loading || index === 0) return;
    setPageError("");
    setPagination({ key, pages, index: index - 1 });
  };
  return {
    data,
    loading: first.loading || busy,
    error: first.error,
    pageError: pagination.key === key ? pageError : "",
    pageNumber: index + 1,
    hasNext: !!data?.hasMore && !!data.nextCursor,
    reload: first.reload,
    next,
    previous,
  };
}
export type AccountTab = "settings" | "billing" | "redeem";
export interface AccountLocation {
  tab: AccountTab;
  billingView: "records" | "research";
  range: "7" | "30" | "all" | "custom";
  type: string;
  researchId: string;
  start: string;
  end: string;
  recordId: string;
}
const TYPES = [
  "",
  "purchase",
  "redeem",
  "checkin",
  "usage",
  "settle",
  "refund",
  "reconciliation",
];
export function readAccountLocation(): AccountLocation {
  const p = new URLSearchParams(
    typeof window === "undefined" ? "" : window.location.search,
  );
  return {
    tab: ["settings", "billing", "redeem"].includes(p.get("tab") ?? "")
      ? (p.get("tab") as AccountTab)
      : "settings",
    billingView: p.get("view") === "research" ? "research" : "records",
    range: ["7", "30", "all", "custom"].includes(p.get("range") ?? "")
      ? (p.get("range") as AccountLocation["range"])
      : "30",
    type: TYPES.includes(p.get("type") ?? "") ? (p.get("type") ?? "") : "",
    researchId: (p.get("research_id") ?? "").slice(0, 512),
    start: p.get("start") ?? "",
    end: p.get("end") ?? "",
    recordId: p.get("record_id") ?? "",
  };
}
export function useAccountLocation() {
  const [value, setValue] = useState(readAccountLocation);
  useEffect(() => {
    const pop = () => setValue(readAccountLocation());
    window.addEventListener("popstate", pop);
    return () => window.removeEventListener("popstate", pop);
  }, []);
  const update = (patch: Partial<AccountLocation>) => {
    const next = { ...value, ...patch };
    const url = new URL(window.location.href);
    const fields = {
      tab: next.tab,
      view: next.billingView === "research" ? "research" : "",
      range: next.range,
      type: next.type,
      research_id: next.researchId,
      start: next.start,
      end: next.end,
      record_id: next.recordId,
    };
    for (const [key, val] of Object.entries(fields)) {
      if (val) url.searchParams.set(key, val);
      else url.searchParams.delete(key);
    }
    window.history.pushState(null, "", url);
    setValue(next);
  };
  return [value, update] as const;
}
export function accountDateFilter(
  value: Pick<AccountLocation, "range" | "start" | "end">,
  now = new Date(),
) {
  if (value.range === "all") return {};
  const today = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Shanghai",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(now);
  const startDate = value.range === "custom" ? value.start : today;
  const endDate = value.range === "custom" ? value.end : today;
  if (
    !/^\d{4}-\d{2}-\d{2}$/.test(startDate) ||
    !/^\d{4}-\d{2}-\d{2}$/.test(endDate)
  )
    throw new Error("请选择起止日期");
  const start = new Date(`${startDate}T00:00:00+08:00`),
    end = new Date(`${endDate}T00:00:00+08:00`);
  if (
    !Number.isFinite(start.getTime()) ||
    !Number.isFinite(end.getTime()) ||
    start > end ||
    new Intl.DateTimeFormat("en-CA", {
      timeZone: "Asia/Shanghai",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(start) !== startDate ||
    new Intl.DateTimeFormat("en-CA", {
      timeZone: "Asia/Shanghai",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(end) !== endDate
  )
    throw new Error("结束日期不能早于开始日期");
  if (value.range !== "custom")
    start.setTime(start.getTime() - (Number(value.range) - 1) * 86400000);
  end.setTime(end.getTime() + 86400000);
  return { start_time: start.toISOString(), end_time: end.toISOString() };
}
